/**
 * My week (3.1): one clinician's seven days, Sunday to Saturday — the pattern
 * she was handed, from her side.
 *
 * Cards come from the effective calendar (so her own moves show before the
 * server confirms them), from the holds placed on her (a morning held for a
 * start of care, with no patient detail), and from every plan placeholder
 * assigned to her — the beyond-auth ones hatched and dashed under the word the
 * week totals count them by, the ones inside the authorization hatched with a
 * solid edge and titled by their visit type. Both are drawn, because both are
 * already in the day's points: what is counted is what is on the day.
 *
 * Nothing on this screen carries another clinician's number.
 */
import { isVisitCancelable } from "@caredna-marlin/utils/rules/visitStatus";
import type { DateKey, DemoState, IsoString, RoundStatus, VisitAnnotation } from "@caredna-marlin/utils/homeHealthDemo/state";
import { addDaysToKey, addWorkingDaysToKey, dateKeyRange, formatInBranchZone, isoWeekday, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { confirmationLabel } from "./confirmation";
import { selectConfirmationRows, type ConfirmationRow } from "./confirmationRound";
import { isPendingId, selectEffectiveCalendar } from "./effectiveCalendar";
import { reservedLabel } from "./reserved";
import { visitTypeLabel } from "./roster";
import { planVisitLayers, selectWeekPoints, type WeekPoints } from "./utilization";
import type { TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";

/** The Sunday the displayed week starts on. Points are an ISO (Monday) week; Dana works no weekends, so the two agree. */
export function sundayOf(key: DateKey): DateKey {
  return addDaysToKey(key, -(isoWeekday(key) % 7));
}

/** `planned` is a placeholder inside the authorization; `provisional` is one beyond it. */
export type WeekCardKind = "visit" | "block" | "hold" | "provisional" | "planned";

export interface WeekCard {
  id: string;
  kind: WeekCardKind;
  date: DateKey;
  /** "HH:mm" branch-zone wall clock. Absent for an all-day hold window. */
  start: string;
  end: string;
  title: string;
  subtitle?: string;
  address?: string;
  realVisitId?: number;
  blockId?: number;
  planVisitId?: string;
  holdId?: string;
  patientRef?: string;
  /** Placeholders and holds are hatched: they occupy a slot and cede it to a real visit. */
  hatched: boolean;
  /**
   * The card holds a genuine window and belongs on My day's time axis. A visit, a
   * block and a hold always do, and so does a reservation the placement search
   * has given an hour. A reservation it could not place has no hour at all —
   * `start` is `""` — and My day draws it in the **Needs a time** tray rather
   * than inventing a position for it.
   */
  onAxis: boolean;
  /** The clinician's own change, not yet confirmed by the server. */
  pending: boolean;
  /** My week's cross-day drag: a real visit she may still move, and nothing else. */
  draggable: boolean;
  /**
   * My day's within-day drag. A real visit, and — since Pete asked for it on
   * 2026-09-18, *"blocks should be draggable just like visits"* — a calendar
   * block, which re-times through the product's own block update. A block is
   * **not** `draggable`: the week's drag moves a card to another day, and a
   * block is dragged on the axis, within the one day it belongs to.
   */
  retimable: boolean;
  /**
   * The card carries its own menu: **Cancel visit** on a visit, **Remove block**
   * on a block. Today and later only — a day already spent offers nothing, the
   * same rule the day header's menu follows — and never for a change the server
   * has not confirmed yet. A visit the product would refuse to cancel (in
   * progress, delivered, already canceled) is not offered it either:
   * `isVisitCancelable` is the product's own rule, so the menu and the server
   * cannot disagree.
   */
  removable: boolean;
  /**
   * The confirmation round's state (3.3), drawn as one icon on the card in both
   * views. Absent on anything the round has never touched.
   */
  roundStatus?: RoundStatus;
  /** The whole label — state and reasons — for the icon's tooltip. Phrased by the view. */
  roundLabel?: TimeSentence;
  /**
   * **Mark confirmed** is on the card's own menu, and only where it means
   * something: a visit the round has texted and the patient has not confirmed.
   */
  markConfirmable: boolean;
  /**
   * The arrival window the patient actually has, which is what My week shows in
   * place of the minute the clinician plans on (Pete, 2026-09-19). Carried as
   * **ISO**, not as a label: the view formats it through the shared helpers, so
   * it follows the user's own twelve/twenty-four-hour setting — which an engine
   * selector cannot read. Absent when the visit has no window; a block, a hold
   * and a placeholder never do.
   */
  arrivalWindowStartIso?: IsoString;
  arrivalWindowEndIso?: IsoString;
}

export interface MyWeekDay {
  date: DateKey;
  weekdayLabel: string;
  dateLabel: string;
  isToday: boolean;
  isPast: boolean;
  cards: WeekCard[];
  /** Drive minutes the product computed for the day's visits. */
  driveMinutes: number;
  points: number;
}

export interface MyWeekView {
  handle: string;
  /** Sunday of the displayed week. */
  weekStart: DateKey;
  days: MyWeekDay[];
  points: WeekPoints;
}

function timeLabel(iso: string, tz: string): string {
  return formatInBranchZone(new Date(iso), tz, "HH:mm");
}

function partOfDay(windowStart: string): string {
  const hour = Number(windowStart.split(":")[0]);
  return hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
}

/**
 * How a patient is named on a card: the cast table's form ("K. Osei"), or the
 * tenant's own if the fixtures do not carry them.
 *
 * Matched on the name rather than through `patientIdsByRef`, which is rebuilt
 * from the horizon read and is empty until it lands — a card that said "Kenneth
 * Osei" and then became "K. Osei" thirty seconds later would be worse than
 * either name on its own.
 */
export function cardName(state: Pick<DemoState, "patients">, patientName: string): string | undefined {
  const name = patientName.trim().toLowerCase();
  if (!name) return undefined;
  return Object.values(state.patients).find((p) => `${p.firstName} ${p.lastName}`.trim().toLowerCase() === name)?.displayName;
}

function patientRefFor(state: Pick<DemoState, "meta">, patientId: number): string | undefined {
  return Object.entries(state.meta.bindings.patientIdsByRef).find(([, id]) => id === patientId)?.[0];
}

/**
 * The round's state for one card. **The round's own row wins wherever there is
 * one**, because that is what My day draws from and it knows two things the
 * stored annotation does not: the implied **Not sent** before the round has
 * gone, and a move that is still staged. Reading the annotation here instead is
 * what let a week column say *Confirmed* while the same visit on My day said
 * *Needs a call · Moved after the text*.
 *
 * Off the next working day there is no round to have a row in, so the stored
 * annotation is all there is — which is what keeps a fired round's icons on the
 * day once tomorrow has become today.
 */
function roundFor(
  state: Pick<DemoState, "config">,
  row: ConfirmationRow | undefined,
  annotation: VisitAnnotation | undefined,
  stillOpen: boolean,
): { roundStatus?: RoundStatus; roundLabel?: TimeSentence; markConfirmable: boolean } {
  const status = row?.status ?? annotation?.round?.status;
  if (!status) return { markConfirmable: false };
  return {
    roundStatus: status,
    roundLabel: row?.label ?? confirmationLabel(state, annotation),
    // Confirmed already needs nothing, and a visit already delivered or called
    // off is not asked about at all — the same rule the card's Cancel visit
    // follows, because there is nothing left to tell a patient about a visit
    // that has happened. Everything else can be confirmed by hand, **including
    // Not sent**: a patient who rings before the text goes out has confirmed
    // just as truly as one who answers it, and `fireRound` skips anybody whose
    // row is no longer `notSent`, so marking one early takes it out of the round
    // rather than texting them a second time.
    markConfirmable: stillOpen && status !== "confirmed",
  };
}

export function selectMyWeek(state: DemoState, handle: string, anchorDate: DateKey, wallNow: Date = new Date()): MyWeekView {
  const tz = state.config.branch.timeZone;
  const today = toDateKey(selectDemoNow(state, wallNow), tz);
  const weekStart = sundayOf(anchorDate);
  const dates = dateKeyRange(weekStart, addDaysToKey(weekStart, 6));
  const providerId = state.meta.bindings.providerIdsByHandle[handle];
  const { visits, blocks, pendingKeys } = selectEffectiveCalendar(state, providerId, { start: dates[0], end: dates[6] });
  const pending = new Set(pendingKeys);
  const isPending = (visitId: number) => pending.has(`visit:${visitId}`) || isPendingId(visitId);
  // The confirmation round is about the next working day and no other, so its
  // rows are read once, here, and only when that day is in the week on screen.
  const nextWorkingDay = addWorkingDaysToKey(today, 1);
  const roundRows = new Map<number, ConfirmationRow>(
    dates.includes(nextWorkingDay) ? selectConfirmationRows(state, providerId, nextWorkingDay).map((r) => [r.realVisitId, r]) : [],
  );
  const layers = planVisitLayers(state);

  const byDate = new Map<DateKey, WeekCard[]>(dates.map((d) => [d, []]));
  const driveByDate = new Map<DateKey, number>(dates.map((d) => [d, 0]));

  for (const block of blocks) {
    const date = toDateKey(new Date(block.startIso), tz);
    if (!byDate.has(date)) continue;
    byDate.get(date)!.push({
      id: `block-${block.id}`,
      kind: "block",
      date,
      start: timeLabel(block.startIso, tz),
      end: timeLabel(block.endIso, tz),
      title: block.title ?? "Block",
      address: block.address,
      blockId: block.id,
      hatched: false,
      onAxis: true,
      pending: isPendingId(block.id) || pending.has(`moveBlock:${block.id}`),
      draggable: false,
      retimable: !isPendingId(block.id) && !pending.has(`moveBlock:${block.id}`) && date >= today,
      removable: !isPendingId(block.id) && date >= today,
      markConfirmable: false,
    });
  }

  for (const vr of visits) {
    const date = toDateKey(new Date(vr.startIso), tz);
    if (!byDate.has(date)) continue;
    const annotation = state.annotations[String(vr.visitId)];
    byDate.get(date)!.push({
      id: `visit-${vr.visitId}`,
      kind: "visit",
      date,
      start: timeLabel(vr.startIso, tz),
      end: timeLabel(vr.endIso, tz),
      // **The fixture's name, when the fixture knows this patient.** The tenant
      // stores "Kenneth Osei" and the cast table the presenter is reading from
      // says "K. Osei", so a real visit and a reservation for the same patient
      // read as two different people on the same week. The reservation below
      // already uses `displayName`; this is the other half of it. A patient the
      // fixtures do not carry keeps the tenant's own name.
      title: cardName(state, vr.patientName) ?? vr.patientName,
      subtitle: vr.visitTypeName,
      realVisitId: vr.visitId,
      patientRef: patientRefFor(state, vr.patientId),
      hatched: false,
      onAxis: true,
      pending: isPending(vr.visitId),
      draggable: !isPendingId(vr.visitId) && date >= today,
      retimable: !isPendingId(vr.visitId) && date >= today,
      removable: !isPendingId(vr.visitId) && date >= today && isVisitCancelable(vr.eventStatus),
      ...roundFor(state, roundRows.get(vr.visitId), annotation, date >= today && isVisitCancelable(vr.eventStatus)),
      arrivalWindowStartIso: vr.arrivalWindowStartIso,
      arrivalWindowEndIso: vr.arrivalWindowEndIso,
    });
    driveByDate.set(date, (driveByDate.get(date) ?? 0) + vr.startOfShiftDriveMinutes + vr.interVisitDriveMinutes + vr.endOfShiftDriveMinutes);
  }

  for (const hold of Object.values(state.holds)) {
    if (hold.status !== "held" || hold.providerHandle !== handle || !byDate.has(hold.date)) continue;
    byDate.get(hold.date)!.push({
      id: `hold-${hold.id}`,
      kind: "hold",
      date: hold.date,
      start: hold.windowStart,
      end: hold.windowEnd,
      title: reservedLabel("heldForSoc"),
      subtitle: partOfDay(hold.windowStart),
      holdId: hold.id,
      hatched: true,
      onAxis: true,
      pending: false,
      draggable: false,
      retimable: false,
      removable: false,
      markConfirmable: false,
    });
  }

  // The same guard `placeholderPointsFor` uses, so the cards drawn on a day and
  // the placeholders counted into it are one set: a placeholder can never be
  // points with nothing to look at.
  for (const visit of Object.values(state.planVisits)) {
    if (layers[visit.id] === undefined || visit.providerHandle !== handle || !byDate.has(visit.targetDate)) continue;
    const episode = state.episodes[visit.episodeId];
    const patient = episode ? state.patients[episode.patientRef] : undefined;
    const patientName = patient?.displayName ?? episode?.patientRef;
    // No fallback hour. A reservation that has been placed carries its own; one
    // the search could not place carries none, and a made-up 13:00 put an
    // assessment-length card through the middle of the day's one open span.
    const start = visit.windowStart ?? "";
    const beyondAuth = layers[visit.id] === "provisional";
    byDate.get(visit.targetDate)!.push({
      id: `plan-${visit.id}`,
      kind: beyondAuth ? "provisional" : "planned",
      date: visit.targetDate,
      start,
      end: visit.windowEnd ?? start,
      // "reserved" is the on-screen word for the beyond-auth layer everywhere
      // since 2026-09-18; the header above this card counts it under that word.
      // The inside-auth card is titled by its visit type and says plainly what
      // is missing — a day — because the header counts it under "points".
      // One word with a reason on every reservation the branch read counts as
      // reserved. An ordinary placeholder inside the authorization is **Planned**
      // on that read, so it keeps its visit type and says plainly what is missing
      // rather than borrowing a word that would contradict the heat map.
      title: beyondAuth ? reservedLabel("pendingAuth") : visit.rule === "recert" ? reservedLabel("recertWindow") : visitTypeLabel(state.config, visit.visitTypeKind),
      subtitle: beyondAuth || visit.rule === "recert" ? patientName : patientName ? `Not yet scheduled · ${patientName}` : "Not yet scheduled",
      planVisitId: visit.id,
      patientRef: episode?.patientRef,
      hatched: true,
      onAxis: visit.windowStart !== undefined,
      pending: false,
      draggable: false,
      retimable: false,
      removable: false,
      markConfirmable: false,
    });
  }

  const points = selectWeekPoints(state, handle, addDaysToKey(weekStart, 1));
  const days: MyWeekDay[] = dates.map((date) => ({
    date,
    weekdayLabel: formatInBranchZone(new Date(`${date}T12:00:00Z`), "UTC", "EEE"),
    dateLabel: formatInBranchZone(new Date(`${date}T12:00:00Z`), "UTC", "MMM d"),
    isToday: date === today,
    isPast: date < today,
    // A card with no hour sorts last in its column, not into the middle of the
    // afternoon: it is waiting for a time, not claiming one.
    cards: (byDate.get(date) ?? []).sort((a, b) => (a.start || "24:00").localeCompare(b.start || "24:00")),
    driveMinutes: driveByDate.get(date) ?? 0,
    points: 0,
  }));
  // Firm points per day come from the one week selector, so the column footers and the header agree.
  const mondayFirst = points.byDay;
  for (const day of days) {
    const index = (isoWeekday(day.date) + 6) % 7;
    day.points = mondayFirst[index] ?? 0;
  }
  return { handle, weekStart, days, points };
}
